import { body, coverBg, edge, fade, gloss, mix, n, shade, shadowInk, tint, type ArtPal } from './kit';

/**
 * Placeholder covers of the four skill games (wave A of docs/four-games-mobile-plan.md) until the final art
 * (docs/art-production-spec.md): calm scenes, the focus object in the right half, no glyphs, letters or numbers
 * (the words and characters of the games are live text, never baked into a picture).
 *   tile-match  — a small pyramid of ivory tiles, a free matching pair lifted and outlined
 *   color-sort  — three glass tubes with ceramic counters, each colour with its own pattern (colour-blind safe)
 *   daily-words — a 4×4 board of blank letter tiles, two found words as straight translucent capsules
 *   chess       — a knight (geometric silhouette) on a corner of a board
 */

const ivoryOf = (P: ArtPal) => (P.dark ? '#F3EAD6' : '#FBF4E4');

function tile(x: number, y: number, w: number, h: number, P: ArtPal, p: string, face: string, lift = false): string {
  const ivory = ivoryOf(P);
  const side = shade(ivory, P.dark ? 0.35 : 0.22);
  const ring = lift ? `<rect x="${n(x - 4)}" y="${n(y - 4)}" width="${n(w + 8)}" height="${n(h + 8)}" rx="12" fill="none" stroke="${P.teal}" stroke-width="3"/>` : '';
  return (
    `<rect x="${n(x + 5)}" y="${n(y + 7)}" width="${n(w)}" height="${n(h)}" rx="9" fill="${side}"/>` +
    `<rect x="${n(x)}" y="${n(y)}" width="${n(w)}" height="${n(h)}" rx="9" fill="url(#${p}iv)" stroke="${edge(ivory, P)}" stroke-width="1.2"/>` +
    face +
    ring
  );
}

export function tileMatchCover(P: ArtPal, p: string): string {
  const top = P.dark ? mix(P.bg, P.green, 0.12) : tint(P.green, 0.9);
  const mid = P.dark ? mix(P.panel, P.teal, 0.08) : tint(P.teal, 0.92);
  const bg = coverBg(p, P, top, mid, P.bg, [450, 200]);
  const W = 62;
  const H = 82;
  const jade = P.dark ? shade(P.teal, 0.3) : P.teal;
  const red = P.red;
  const blue = P.dark ? shade(P.blue, 0.05) : P.blue;
  const circle = (x: number, y: number, c: string) =>
    `<circle cx="${n(x + W / 2)}" cy="${n(y + H / 2)}" r="18" fill="${c}"/><circle cx="${n(x + W / 2)}" cy="${n(y + H / 2)}" r="10" fill="${ivoryOf(P)}"/><circle cx="${n(x + W / 2)}" cy="${n(y + H / 2)}" r="5" fill="${c}"/>`;
  const bars = (x: number, y: number, c: string) =>
    [0, 1, 2].map((i) => `<rect x="${n(x + 16 + i * 12)}" y="${n(y + 20)}" width="7" height="42" rx="3.5" fill="${c}"/>`).join('');
  const diamond = (x: number, y: number, c: string) =>
    `<polygon points="${n(x + W / 2)},${n(y + 18)} ${n(x + W - 14)},${n(y + H / 2)} ${n(x + W / 2)},${n(y + H - 18)} ${n(x + 14)},${n(y + H / 2)}" fill="${c}"/>`;
  // bottom row, middle row, top pair (the free matching pair is lifted)
  const row1 = [300, 366, 432, 498].map((x, i) => tile(x, 238, W, H, P, p, [bars, circle, diamond, bars][i]!(x, 238, [jade, blue, red, jade][i]!)));
  const row2 = [333, 399, 465].map((x, i) => tile(x, 172, W, H, P, p, [diamond, bars, circle][i]!(x, 172, [blue, red, blue][i]!)));
  const pair = [tile(366, 92, W, H, P, p, circle(366, 92, red), true), tile(432, 98, W, H, P, p, circle(432, 98, red), true)];
  return (
    `<defs>${bg.defs}${body(`${p}iv`, ivoryOf(P), 0.3, 0.12)}${fade(`${p}sh`, shadowInk(P), P.dark ? 0.55 : 0.25)}</defs>` +
    bg.body +
    `<ellipse cx="430" cy="335" rx="190" ry="26" fill="url(#${p}sh)"/>` +
    row1.join('') +
    row2.join('') +
    pair.join('') +
    gloss(392, 108, 14, 6, -20, 0.5)
  );
}

export function colorSortCover(P: ArtPal, p: string): string {
  const top = P.dark ? mix(P.bg, P.purple, 0.14) : tint(P.purple, 0.9);
  const mid = P.dark ? mix(P.panel, P.orange, 0.06) : tint(P.orange, 0.92);
  const bg = coverBg(p, P, top, mid, P.bg, [450, 200]);
  const glass = P.dark ? tint(P.sky, 0.2) : shade(P.sky, 0.1);
  const colors = [P.orange, P.teal, P.purple];
  // the pattern of each colour: dot, ring, stripe — readable without colour
  const mark = (cx: number, cy: number, k: number) => {
    const ink = ivoryOf(P);
    if (k === 0) return `<circle cx="${n(cx)}" cy="${n(cy)}" r="5" fill="${ink}" fill-opacity=".85"/>`;
    if (k === 1) return `<circle cx="${n(cx)}" cy="${n(cy)}" r="7" fill="none" stroke="${ink}" stroke-opacity=".85" stroke-width="2.6"/>`;
    return `<rect x="${n(cx - 10)}" y="${n(cy - 2.5)}" width="20" height="5" rx="2.5" fill="${ink}" fill-opacity=".85"/>`;
  };
  const tube = (x: number, stack: number[], lifted = 0) => {
    const y0 = 96 - lifted;
    const balls = stack
      .map((k, i) => {
        const cy = y0 + 196 - i * 44;
        return `<circle cx="${x}" cy="${cy}" r="19" fill="url(#${p}b${k})"/>${mark(x, cy, k)}`;
      })
      .join('');
    return (
      `<rect x="${x - 28}" y="${y0}" width="56" height="232" rx="26" fill="${glass}" fill-opacity="${P.dark ? 0.12 : 0.18}" stroke="${glass}" stroke-opacity=".7" stroke-width="2.5"/>` +
      balls +
      `<rect x="${x - 20}" y="${y0 + 12}" width="7" height="180" rx="3.5" fill="#FFFFFF" fill-opacity=".22"/>`
    );
  };
  return (
    `<defs>${bg.defs}${colors.map((c, i) => body(`${p}b${i}`, c, 0.35, 0.3)).join('')}${fade(`${p}sh`, shadowInk(P), P.dark ? 0.55 : 0.25)}</defs>` +
    bg.body +
    `<ellipse cx="440" cy="340" rx="170" ry="22" fill="url(#${p}sh)"/>` +
    tube(350, [0, 1, 0, 2]) +
    tube(440, [1, 1, 1], 18) +
    tube(530, [2, 0, 2]) +
    `<circle cx="440" cy="60" r="19" fill="url(#${p}b1)"/>` +
    mark(440, 60, 1)
  );
}

export function dailyWordsCover(P: ArtPal, p: string): string {
  const top = P.dark ? mix(P.bg, P.blue, 0.16) : tint(P.blue, 0.9);
  const mid = P.dark ? mix(P.panel, P.purple, 0.08) : tint(P.purple, 0.93);
  const bg = coverBg(p, P, top, mid, P.bg, [450, 200]);
  const S = 58;
  const G = 8;
  const x0 = 318;
  const y0 = 62;
  // two found words, straight as in the game: a diagonal and a row
  const diag = new Set(['0,0', '1,1', '2,2', '3,3']);
  const row = new Set(['3,0', '3,1', '3,2']);
  const centre = (r: number, c: number) => [x0 + c * (S + G) + S / 2, y0 + r * (S + G) + S / 2] as const;
  const capsule = (a: readonly [number, number], b: readonly [number, number], col: string) => {
    const [x1, y1] = centre(...a);
    const [x2, y2] = centre(...b);
    return `<line x1="${n(x1)}" y1="${n(y1)}" x2="${n(x2)}" y2="${n(y2)}" stroke="${col}" stroke-width="${S - 6}" stroke-linecap="round" stroke-opacity="${P.dark ? 0.4 : 0.32}"/>`;
  };
  const cells: string[] = [];
  for (let r = 0; r < 4; r++) {
    for (let c = 0; c < 4; c++) {
      const x = x0 + c * (S + G);
      const y = y0 + r * (S + G);
      const on = diag.has(`${r},${c}`) || row.has(`${r},${c}`);
      cells.push(
        `<rect x="${x + 3}" y="${y + 5}" width="${S}" height="${S}" rx="12" fill="${shadowInk(P)}" fill-opacity="${P.dark ? 0.35 : 0.12}"/>` +
          `<rect x="${x}" y="${y}" width="${S}" height="${S}" rx="12" fill="url(#${p}iv)" stroke="${edge(ivoryOf(P), P)}" stroke-width="1"/>` +
          // an abstract stroke instead of a letter
          `<rect x="${x + 18}" y="${y + 26}" width="${S - 36}" height="6" rx="3" fill="${shade(ivoryOf(P), on ? 0.6 : 0.45)}" fill-opacity="${on ? 0.85 : 0.5}"/>`,
      );
    }
  }
  return (
    `<defs>${bg.defs}${body(`${p}iv`, ivoryOf(P), 0.3, 0.12)}</defs>` +
    bg.body +
    cells.join('') +
    capsule([0, 0], [3, 3], P.teal) +
    capsule([3, 0], [3, 2], P.orange)
  );
}

export function chessCover(P: ArtPal, p: string): string {
  const top = P.dark ? mix(P.bg, P.sky, 0.1) : tint(P.sky, 0.9);
  const mid = P.dark ? mix(P.panel, P.gold, 0.05) : tint(P.gold, 0.93);
  const bg = coverBg(p, P, top, mid, P.bg, [450, 200]);
  const light = P.dark ? mix(P.panel, '#FFFFFF', 0.22) : '#EDE3CF';
  const dark = P.dark ? mix(P.panel, P.sky, 0.25) : shade('#B58863', 0.05);
  const cells: string[] = [];
  const C = 52;
  for (let r = 0; r < 3; r++) {
    for (let c = 0; c < 6; c++) {
      cells.push(`<rect x="${270 + c * C}" y="${248 + r * C}" width="${C}" height="${C}" fill="${(r + c) % 2 ? dark : light}"/>`);
    }
  }
  const ivory = ivoryOf(P);
  // a knight from straight lines and arcs: base, neck, head, ear, mane
  const knight =
    `<path d="M392 300 h116 a10 10 0 0 0 -8 -16 h-100 a10 10 0 0 0 -8 16z" fill="url(#${p}kn)"/>` +
    `<path d="M410 284 C404 236 420 212 446 196 L430 190 C416 184 404 168 412 150 L440 118 C452 104 470 98 486 100 L494 86 L504 104 C526 120 540 156 532 196 C526 232 500 252 494 284 Z" fill="url(#${p}kn)" stroke="${edge(ivory, P)}" stroke-width="2"/>` +
    `<circle cx="458" cy="132" r="5" fill="${shade(ivory, 0.6)}"/>` +
    `<path d="M496 112 C514 132 520 170 512 206" fill="none" stroke="${shade(ivory, 0.3)}" stroke-width="4" stroke-linecap="round"/>`;
  return (
    `<defs>${bg.defs}${body(`${p}kn`, ivory, 0.25, 0.3)}${fade(`${p}sh`, shadowInk(P), P.dark ? 0.6 : 0.3)}</defs>` +
    bg.body +
    `<g opacity="${P.dark ? 0.9 : 1}">${cells.join('')}</g>` +
    `<ellipse cx="450" cy="302" rx="78" ry="12" fill="url(#${p}sh)"/>` +
    knight +
    gloss(452, 150, 12, 26, -30, 0.45)
  );
}
